/**
 * Nostr identity helpers for the browser.
 *
 * Three login paths converge on the same outcome (server-set cookie):
 *   1. Generate a fresh identity server-side (POST /auth/nostr/generate).
 *   2. Paste an existing nsec (POST /auth/nostr/nsec-login).
 *   3. NIP-07 browser extension (window.nostr) — we read the pubkey from the
 *      extension, but the actual auth still goes through the challenge/verify
 *      flow OR nsec-login. Since NIP-07 never exposes the secret key, the
 *      cleanest path for a cookie-based Worker is: extension signs a challenge.
 *      That flow lives in the Worker (/auth/nostr/challenge + /auth/nostr/verify).
 *
 * For v1 of this SPA we wire paths 1 and 2 fully, and detect NIP-07 so the UI
 * can offer it. Full challenge/verify wiring is a fast-follow.
 */

import { generateIdentity, nsecLogin, getStoredNsec, storeNsec, clearStoredNsec } from './api';

export interface NostrIdentity {
  pubkey: string;
  npub: string;
  /** Whether the secret key is held locally (paths 1+2) vs only in an extension (path 3). */
  hasLocalSecret: boolean;
}

/** True if a NIP-07 browser extension (window.nostr) is available. */
export function hasNip07(): boolean {
  return typeof window !== 'undefined' && typeof window.nostr !== 'undefined';
}

/** Read the active pubkey from a NIP-07 extension, if present. */
export async function getExtensionPublicKey(): Promise<string | null> {
  if (!hasNip07() || !window.nostr) return null;
  try {
    return await window.nostr.getPublicKey();
  } catch {
    return null;
  }
}

/** True if we have a locally-stored nsec from a prior generate/paste. */
export function hasStoredIdentity(): boolean {
  return getStoredNsec() !== null;
}

/**
 * Generate a brand new Nostr identity. Server returns nsec + sets the session
 * cookie. We persist the nsec locally so the user can recover the identity.
 */
export async function generateNewIdentity(): Promise<NostrIdentity> {
  const result = await generateIdentity();
  storeNsec(result.nsec);
  return {
    pubkey: result.pubkey,
    npub: result.npub,
    hasLocalSecret: true,
  };
}

/** Log in with a pasted nsec. Server validates + sets the session cookie. */
export async function loginWithNsec(nsec: string): Promise<NostrIdentity> {
  const result = await nsecLogin(nsec);
  storeNsec(nsec);
  return {
    pubkey: result.pubkey,
    npub: result.npub,
    hasLocalSecret: true,
  };
}

/** Forget the locally-stored nsec. Does NOT log out the server session. */
export function forgetLocalIdentity(): void {
  clearStoredNsec();
}

// ─── NIP-07 type shim ───────────────────────────────────────────────────

declare global {
  interface Window {
    nostr?: {
      getPublicKey(): Promise<string>;
      signEvent(event: unknown): Promise<unknown>;
      getRelays?(): Promise<Record<string, unknown>>;
      nip04?: {
        encrypt(pubkey: string, plaintext: string): Promise<string>;
        decrypt(pubkey: string, ciphertext: string): Promise<string>;
      };
    };
  }
}
